/**
 * SMS opt-outs
 *
 * Who must not be texted, and why.
 *
 * Two things put a number in here. Someone replies STOP, which arrives through
 * the provider webhook and is written server-side. Or Tall Bob rejects a send
 * with "recipient opted out" (code 9998), which means they opted out somewhere
 * we never saw — through the carrier, or on a message from a different sender.
 * Either way the number is dead to us until a person removes it by hand.
 *
 * The check runs before every send. It costs one indexed lookup, which is a
 * small price for not texting someone who has asked twice.
 */

import { supabase } from '../lib/supabase'

export interface SmsOptOut {
  id: string
  number: string
  reason: string | null
  source: 'reply' | 'provider' | 'manual' | string
  note: string | null
  created_at: string
}

/** Digits only, no plus — the form the opt-out table is keyed on. */
export function optOutKey(phone?: string | null): string | null {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return null
  if (digits.startsWith('61') && digits.length === 11) return digits
  if (digits.startsWith('0') && digits.length === 10) return `61${digits.slice(1)}`
  if (digits.length === 9 && !digits.startsWith('0')) return `61${digits}`
  return digits
}

/**
 * Has this number opted out?
 *
 * Returns false when the lookup itself fails. That is deliberate: a database
 * hiccup should not silently stop the AR ladder for everyone. The provider is
 * the backstop — it rejects an opted-out recipient anyway, and that rejection
 * is what writes the row in the first place.
 */
export async function isOptedOut(phone?: string | null): Promise<boolean> {
  const key = optOutKey(phone)
  if (!key) return false
  try {
    const { data, error } = await supabase
      .from('sms_opt_outs')
      .select('id')
      .eq('number', key)
      .limit(1)
    if (error) {
      console.error('[SMS] opt-out lookup failed:', error.message)
      return false
    }
    return Boolean(data && data.length)
  } catch (err) {
    console.error('[SMS] opt-out lookup threw:', err)
    return false
  }
}

/**
 * Record an opt-out. Idempotent — the number is unique, so a second call for
 * the same number is a no-op rather than an error.
 */
export async function recordOptOut(
  phone: string,
  source: 'reply' | 'provider' | 'manual',
  reason?: string | null,
  note?: string | null
): Promise<void> {
  const key = optOutKey(phone)
  if (!key) return
  try {
    const { error } = await supabase
      .from('sms_opt_outs')
      .upsert({ number: key, source, reason: reason?.slice(0, 64) || null, note: note || null }, { onConflict: 'number' })
    if (error) console.error('[SMS] could not record opt-out:', error.message)
  } catch (err) {
    console.error('[SMS] recording opt-out threw:', err)
  }
}

export async function listOptOuts(): Promise<SmsOptOut[]> {
  const { data, error } = await supabase
    .from('sms_opt_outs')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) {
    console.error('[SMS] could not list opt-outs:', error.message)
    return []
  }
  return (data || []) as SmsOptOut[]
}

/**
 * Take a number off the list.
 *
 * Only ever done by a person, and only when they know the recipient has asked
 * to be contacted again — which is why there is no automatic path back.
 */
export async function removeOptOut(id: string): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.from('sms_opt_outs').delete().eq('id', id)
  return error ? { ok: false, error: error.message } : { ok: true }
}
